import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AllianceThumbnailFallback from './AllianceThumbnailFallback';

/**
 * Alliance 공통 썸네일 영역
 * - 이미지, 우측 상단 태그(Pre-Alliance, Alliance 등), 이미지 바꾸기 버튼, 배경색 컬러피커
 * - 스타일: alliance-common.css .alli.alli-tag 참고(태그), 나머지는 로컬 스타일
 *
 * @param {string} [src] - 썸네일 이미지 URL
 * @param {string} [tag] - 우측 상단 배지 문구 (Pre-Alliance, Alliance, 기타 등), 부모에서 전달
 * @param {string} [tagClassName] - 배지에 추가 클래스 (예: 운영 일시 중지 스타일)
 * @param {string} [backgroundColor] - 배경색(hex 등), 제어용
 * @param {string} [defaultBackgroundColor='#f1f5f9'] - 초기/비제어 시 배경색
 * @param {function} [onImageChange] - (newThumbUrl: string) => void, 이미지 선택 시 object URL 전달
 * @param {function} [onFileChange] - (file: File) => void, 이미지 선택 시 File 전달 (폼 등에서 사용)
 * @param {function} [onBackgroundColorChange] - (color: string) => void, 배경색 변경 시
 * @param {function} [onSelectionChange] - DB 저장용 최종 선택 정보. (payload: { type: 'image', imageUrl: string, file: File } | { type: 'background', backgroundColor: string }) => void
 * @param {boolean} [showImageButton=true] - 이미지 바꾸기 버튼 노출 (onImageChange 있을 때만 유효)
 * @param {boolean} [showColorPicker=false] - 배경색 컬러피커 노출
 * @param {'list'|'detail'} [variant='list'] - 예약 (향후 확장용)
 * @param {string} [className] - 루트 wrapper 추가 클래스
 * @param {number|string} [height] - 영역 높이(px 등), 미지정 시 150
 */
const AllianceThumbnail = ({
  src,
  tag,
  tagClassName = '',
  backgroundColor: controlledBg,
  defaultBackgroundColor = '#f1f5f9',
  onImageChange,
  onFileChange,
  onBackgroundColorChange,
  onSelectionChange,
  showImageButton = true,
  showColorPicker = false,
  variant = 'list',
  className = '',
  height = 150
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const objectUrlRef = useRef(null);
  /** 이미지 바꾸기로 선택한 object URL. 부모 src 반영 전에도 즉시 표시하기 위함 */
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [internalBg, setInternalBg] = useState(defaultBackgroundColor);
  /** true: 배경색만 표시(이미지 숨김), false: 이미지 표시. 배경색 선택 시 true, 이미지 업로드 시 false */
  const [showBackgroundOnly, setShowBackgroundOnly] = useState(false);
  /** src가 실제로 로드 가능한지 여부 (onload 성공시에만 true) */
  const [imageLoadOk, setImageLoadOk] = useState(false);

  const backgroundColor = controlledBg !== undefined && controlledBg !== '' ? controlledBg : internalBg;

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  const handleImageClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (!file || !file.type.startsWith('image/')) return;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      const url = URL.createObjectURL(file);
      objectUrlRef.current = url;
      setSelectedImageUrl(url);
      setShowBackgroundOnly(false);
      setInternalBg(defaultBackgroundColor);
      onImageChange?.(url);
      onFileChange?.(file);
      onBackgroundColorChange?.(defaultBackgroundColor);
      onSelectionChange?.({ type: 'image', imageUrl: url, file });
      e.target.value = '';
    },
    [onImageChange, onFileChange, onBackgroundColorChange, onSelectionChange, defaultBackgroundColor]
  );

  const handleColorChange = useCallback(
    (e) => {
      const color = e.target?.value ?? defaultBackgroundColor;
      setInternalBg(color);
      setShowBackgroundOnly(true);
      setSelectedImageUrl(null);
      onBackgroundColorChange?.(color);
      onSelectionChange?.({ type: 'background', backgroundColor: color });
    },
    [onBackgroundColorChange, onSelectionChange, defaultBackgroundColor]
  );

  const displaySrc = (selectedImageUrl || src || '').trim();
  const canShowImageBtn = showImageButton && (typeof onImageChange === 'function' || typeof onFileChange === 'function');
  const canShowColorPicker = showColorPicker || typeof onBackgroundColorChange === 'function';
  const showFallback = !showBackgroundOnly && !imageLoadOk;
  const showImageLayer = !showBackgroundOnly && imageLoadOk && Boolean(displaySrc);

  useEffect(() => {
    if (showBackgroundOnly) {
      setImageLoadOk(false);
      return;
    }
    if (!displaySrc) {
      setImageLoadOk(false);
      return;
    }
    if (displaySrc.startsWith('blob:') || displaySrc.startsWith('data:')) {
      setImageLoadOk(true);
      return;
    }

    setImageLoadOk(false);
    const img = new Image();
    let cancelled = false;
    img.onload = () => {
      if (!cancelled) setImageLoadOk(true);
    };
    img.onerror = () => {
      if (!cancelled) setImageLoadOk(false);
    };
    img.src = displaySrc;

    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [displaySrc, showBackgroundOnly]);

  const thumbStyle = {
    height: typeof height === 'number' ? `${height}px` : height,
    backgroundColor,
  };

  return (
    <>
      <div
        className={`alliance-thumbnail alli ${className}`.trim()}
        style={thumbStyle}
        role="img"
        aria-label={t('components.common.thumbnail')}
      >
        {showFallback && (
          <AllianceThumbnailFallback />
        )}
        {showImageLayer && (
          <img
            src={displaySrc}
            alt=""
            className="alliance-thumbnail-image-layer"
            decoding="async"
            draggable={false}
            onError={() => setImageLoadOk(false)}
          />
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          aria-label={t('alli.common.selectThumbnailImage')}
          className="alliance-thumbnail-input-hidden"
          onChange={handleFileChange}
        />
        {tag != null && String(tag).trim() !== '' && (
          <span className={`alliance-thumbnail-tag ${tagClassName}`.trim()}>
            {String(tag).trim()}
          </span>
        )}
        <div className="alliance-thumbnail-actions">
          {canShowColorPicker && (
            <label className="alliance-thumbnail-color-wrap" title={t('alli.common.changeBackgroundColor')}>
              <input
                type="color"
                value={backgroundColor}
                onChange={handleColorChange}
                className="alliance-thumbnail-color-input"
                aria-label={t('alli.common.selectBackgroundColor')}
              />
              <span className="alliance-thumbnail-color-label">{t('alli.common.backgroundColor')}</span>
            </label>
          )}
          {canShowImageBtn && (
            <button
              type="button"
              className="alliance-thumbnail-change-btn"
              onClick={handleImageClick}
            >
              {t('alli.common.changeImage')}
            </button>
          )}
        </div>
      </div>
      <style>{`
        .alliance-thumbnail {
          position: relative;
          width: 100%;
          overflow: hidden;
        }
        .alliance-thumbnail-input-hidden {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0,0,0,0);
          white-space: nowrap;
          border: 0;
        }
        .alliance-thumbnail-image-layer {
          position: absolute;
          inset: 0;
          z-index: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .alliance-thumbnail-tag {
          position: absolute;
          top: 12px;
          right: 12px;
          z-index: 1;
          padding: 4px 10px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 500;
          background: #f1f5f9;
          border: 1px solid #e5e7eb;
          color: #475569;
        }
        .alliance-thumbnail-tag.alliance-thumbnail-tag--operation-paused {
          background: #fffbeb;
          color: #b45309;
          border: 1px solid #fcd34d;
          font-weight: 600;
        }
        .alliance-thumbnail-actions {
          position: absolute;
          bottom: 12px;
          left: 12px;
          right: 12px;
          z-index: 1;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 8px;
        }
        .alliance-thumbnail-color-wrap,
        .alliance-thumbnail-change-btn {
          min-height: 36px;
          padding: 6px 12px;
          border-radius: 6px;
          box-sizing: border-box;
        }
        .alliance-thumbnail-color-wrap {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: rgba(0,0,0,0.5);
          border: 1px solid rgba(255,255,255,0.3);
          cursor: pointer;
          color: #fff;
          font-size: 12px;
        }
        .alliance-thumbnail-color-wrap:hover {
          background: rgba(0,0,0,0.65);
        }
        .alliance-thumbnail-color-input {
          width: 22px;
          height: 22px;
          padding: 0;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          background: transparent;
        }
        .alliance-thumbnail-color-input::-webkit-color-swatch-wrapper { padding: 0; }
        .alliance-thumbnail-color-input::-webkit-color-swatch { border: 1px solid rgba(255,255,255,0.5); border-radius: 4px; }
        .alliance-thumbnail-color-label {
          flex-shrink: 0;
        }
        .alliance-thumbnail-change-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          cursor: pointer;
          border: 1px solid rgba(255,255,255,0.4);
          background: rgba(0,0,0,0.5);
          color: #fff;
        }
        .alliance-thumbnail-change-btn:hover {
          background: rgba(0,0,0,0.65);
          color: #fff;
          border-color: rgba(255,255,255,0.5);
        }
      `}</style>
    </>
  );
};

export default AllianceThumbnail;
